import { Button } from '@consigli/facade';
import {
  useProjectId,
  usePackageId,
  useUpdateFindingMutation,
  useUpdateBatchFindingsMutation,
  useGetFindingsUniqueLabelsQuery,
} from '@consigli/hooks';
import { Finding } from '@consigli/types';
import {
  FC,
  useRef,
  useState,
  useEffect,
  MouseEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { LuTicket } from 'react-icons/lu';
import { toast } from 'react-toastify';

import { CheckableFinding } from '@/util/types';

interface AddLabelButtonProps {
  findings: Finding[] | CheckableFinding[];
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onComplete?: () => void;
}

export const AddLabelButton: FC<AddLabelButtonProps> = ({
  findings,
  onClick,
  onKeyDown,
  onComplete,
}) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const packageId = usePackageId();
  const [updateFinding] = useUpdateFindingMutation();
  const [updateBatchFindings] = useUpdateBatchFindingsMutation();

  const [isInputActive, setIsInputActive] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [buttonWidth, setButtonWidth] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: uniqueLabels = [] } = useGetFindingsUniqueLabelsQuery({
    projectId,
    packageId,
  });

  const isBatchMode = useMemo(() => findings.length > 1, [findings.length]);

  // Filter labels based on input value
  const availableLabels = useMemo(
    () =>
      uniqueLabels.filter((label) => {
        // For batch mode, show all labels that match the input
        if (isBatchMode) {
          return label.toLowerCase().includes(inputValue.toLowerCase());
        }
        // For single finding mode, filter out labels already applied to the finding
        return (
          findings[0]?.labels?.includes?.(label) !== true &&
          label.toLowerCase().includes(inputValue.toLowerCase())
        );
      }),
    [uniqueLabels, inputValue, isBatchMode, findings],
  );

  // Capture initial width when component mounts and when active state changes
  useEffect(() => {
    if (buttonRef.current && !isInputActive) {
      const width = buttonRef.current.offsetWidth;
      setButtonWidth(width > 0 ? width : buttonWidth);
    }
  }, [isInputActive, buttonWidth]);

  useEffect(() => {
    // Focus the input when it becomes active
    if (isInputActive && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isInputActive]);

  useEffect(() => {
    // Calculate dropdown position when it opens
    if (isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isDropdownOpen, inputValue]);

  useEffect(() => {
    // Handle clicking outside to close dropdown and input
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        // Check if click is inside dropdown
        !document.getElementById('label-dropdown')?.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setIsInputActive(false);
        setInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addLabel = useCallback(
    async (label: string) => {
      try {
        if (!label.trim()) return;

        if (isBatchMode) {
          // Batch update mode
          // Prepare data for batch update
          const data = findings.map((finding) => {
            // Don't add if already exists
            if (!finding.labels?.includes?.(label)) {
              return {
                id: finding.id,
                labels: [...(finding.labels || []), label],
              };
            }
            // If already has the label, just return the ID to keep the request valid
            return {
              id: finding.id,
            };
          });

          // Filter out findings that don't need updates (already have the label)
          const findingsToUpdate = data.filter((item) => 'labels' in item);

          if (findingsToUpdate.length === 0) {
            toast.info(t('toast.label-already-exists'));
            setIsInputActive(false);
            setIsDropdownOpen(false);
            setInputValue('');
            return;
          }

          await updateBatchFindings({
            projectId,
            packageId,
            data,
          }).unwrap();

          toast.info(t('toast.label-added-to-batch-success'));
          if (onComplete) onComplete();
        } else {
          // Single finding mode
          const finding = findings[0];
          // Don't add if already exists
          if (finding.labels?.includes?.(label)) {
            setIsInputActive(false);
            setIsDropdownOpen(false);
            setInputValue('');
            return;
          }

          const updatedLabels = [...(finding.labels || []), label];

          await updateFinding({
            projectId,
            packageId,
            findingId: finding.id,
            data: {
              labels: updatedLabels,
            },
          }).unwrap();

          toast.info(t('toast.label-added-success'));
        }

        setIsInputActive(false);
        setIsDropdownOpen(false);
        setInputValue('');
      } catch (error) {
        console.log('Error adding label:', error);
        if (isBatchMode) {
          toast.error(t('toast.label-added-to-batch-failure'));
        } else {
          toast.error(t('toast.label-added-failure'));
        }
      }
    },
    [
      isBatchMode,
      findings,
      t,
      projectId,
      packageId,
      updateBatchFindings,
      updateFinding,
      onComplete,
    ],
  );

  const handleButtonClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (onClick) onClick(e);
      setIsInputActive(true);
      setIsDropdownOpen(true);
    },
    [onClick],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsDropdownOpen(true);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && inputValue.trim()) {
        addLabel(inputValue.trim());
      } else if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        setIsInputActive(false);
        setInputValue('');
      }
    },
    [inputValue, addLabel],
  );

  const handleButtonKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
      }
      if (onKeyDown) onKeyDown(e);
    },
    [onKeyDown],
  );

  const handleLabelClick = useCallback(
    (label: string) => {
      addLabel(label);
    },
    [addLabel],
  );

  const renderDropdown = useCallback(() => {
    if (!isDropdownOpen) return null;

    const dropdown = (
      <div
        id="label-dropdown"
        className="fixed z-[100] bg-white border border-day-secondary rounded-sm shadow-lg max-h-[150px] overflow-y-auto"
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
        }}
      >
        {inputValue.trim() && (
          <div className="px-1 pt-1">
            <button
              className="w-full text-left hover:bg-day-light-3 p-1 cursor-pointer flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                handleLabelClick(inputValue.trim());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  handleLabelClick(inputValue.trim());
                }
              }}
              tabIndex={0}
              title={`${inputValue.trim()} (${t('findings.labels.new')})`}
            >
              <div className="text-xs px-1 py-0.5 border border-day-light-1 rounded-sm overflow-hidden whitespace-nowrap text-ellipsis max-w-[140px] inline-block bg-white">
                {inputValue.trim()}{' '}
                <span className="text-day-neutral-subtle">({t('findings.labels.new')})</span>
              </div>
            </button>
          </div>
        )}
        <div className="text-xs font-bold pb-1 text-left mb-1 p-2">
          {t('findings.labels.recent')}
        </div>
        <div>
          {availableLabels.length > 0 ? (
            <ul className="list-none p-0 m-0">
              {availableLabels.map((label) => (
                <li key={label}>
                  <button
                    className="w-full text-left hover:bg-day-light-3 px-2 py-1 cursor-pointer flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLabelClick(label);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.stopPropagation();
                        handleLabelClick(label);
                      }
                    }}
                    tabIndex={0}
                    title={label}
                  >
                    <div className="text-xs px-1 py-0.5 border border-day-light-1 rounded-sm overflow-hidden whitespace-nowrap text-ellipsis max-w-[140px] inline-block bg-white">
                      {label}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-2 py-1 text-xs text-day-neutral-subtle">
              {t('findings.labels.no-results')}
            </div>
          )}
        </div>
      </div>
    );

    return createPortal(dropdown, document.body);
  }, [isDropdownOpen, dropdownPosition, inputValue, t, availableLabels, handleLabelClick]);

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={isBatchMode ? 'w-auto min-w-[160px]' : 'w-[160px]'}
        ref={buttonRef}
        style={isBatchMode && buttonWidth > 0 && isInputActive ? { width: `${buttonWidth}px` } : {}}
      >
        {!isInputActive ? (
          <Button
            secondary
            rounded
            onClick={handleButtonClick}
            onKeyDown={handleButtonKeyDown}
            className={`px-2 py-1 bg-day-light-4 border-1 rounded-md ${isBatchMode ? '' : 'w-full'} min-h-[30px] h-auto justify-start whitespace-normal`}
            icon={LuTicket}
            iconSize={16}
          >
            <span className="font-bold text-sm hyphens-auto"> {t('findings.labels.assign')}</span>
          </Button>
        ) : (
          <div
            className={`flex items-center ${isBatchMode ? 'w-full' : 'w-[160px]'} h-[30px] px-2 py-1 bg-day-light-4 border-1 border-day-secondary rounded-md`}
          >
            <LuTicket className="mr-2 flex-shrink-0 text-day-neutral-dark" size={16} />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={t('findings.labels.search')}
              className="text-xs focus:outline-none focus:border-day-primary w-full bg-transparent text-day-neutral-dark placeholder:text-day-neutral-subtle"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </div>
      {renderDropdown()}
    </div>
  );
};
